import { Box, Button, Paper, TableContainer, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chip from '../../../components/Chip/Chip';
import DefaultTable from '../../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../../components/DefaultTable/TableToolbar/TableToolbar';
import GlobalSearch from '../../../components/GlobalSearch/GlobalSearch';
import ActivateModal from '../../../components/Modals/ActivateModal';
import CredentialsModal from '../../../components/Modals/CredentialsModal';
import DeactivateModal from '../../../components/Modals/DeactivateModal';
import SuccessModal from '../../../components/Modals/SuccessModal';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { ManagerStatus } from '../../../enums/managerStatus';
import { useShowFilterOrSearch } from '../../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../../hooks/useTableFunctions';
import { HeadCell } from '../../../models/headCell';
import { Manager } from '../../../models/manager';
import { Pagination } from '../../../models/pagination';
import { Project } from '../../../models/project';
import {
  ADMINISTRATION_MANAGERS,
  ADMINISTRATION_MANAGERS_CREATE,
  ADMINISTRATION_MANAGERS_EDIT
} from '../../../router/constants';
import { managerService } from '../../../services/managerService';
import { userService } from '../../../services/userService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../../utils/helpers';
import { getRoundStatusStyle } from '../../../utils/statusRoundStyle';
import CreateManager from './CreateManager/CreateManager';
import FilterManagers, { FilterManagersForm } from './FilterManagers';
import { useUserRoles } from '../../../hooks/useUserRoles';
import { ManagerRole } from '../../../enums/managerRole';
import LoginAsModal from '../../../components/Modals/LoginAsModal';

const initialManagersColumnsData: HeadCell[] = [
  {
    id: 'role',
    label: 'Role',
    sortable: true,
    visible: true,
    render: (manager: Manager) => manager.role && ManagerRole[manager.role]
  },
  {
    id: 'firstName',
    label: 'Full Name',
    sortable: true,
    visible: true,
    render: (manager: Manager) =>
      manager.firstName && manager.firstName + ' ' + manager.lastName
  },
  {
    id: 'email',
    label: 'Email Address',
    sortable: true,
    visible: true,
    thStyle: { width: 350 },
    render: (manager: Manager) => manager.email
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    thStyle: { width: 200, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (manager: Manager) => (
      <Box
        sx={getRoundStatusStyle(
          ManagerStatus[manager.active ? 'ACTIVE' : 'INACTIVE']
        )}
      >
        {ManagerStatus[manager.active ? 'ACTIVE' : 'INACTIVE']}
      </Box>
    )
  }
];

const ManagersList = (): JSX.Element => {
  const initialFilterData: FilterManagersForm = {
    managers: [],
    firstName: '',
    lastName: '',
    email: '',
    active: ''
  };
  const { isAdmin } = useUserRoles();
  const [selectedManagers, setSelectedManagers] = useState<Manager[]>([]);
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [deactivateModalData, setDeactivateModalData] = useState<{
    open: boolean;
    user?: Manager;
  }>({ open: false });
  const [activateModalData, setActivateModalData] = useState<{
    open: boolean;
    user?: Manager;
  }>({ open: false });
  const [addUserModalData, setAddUserModalData] = useState<{
    open: boolean;
    project?: Project;
  }>({ open: false });
  const [resendModalData, setResendModalData] = useState<{
    open: boolean;
    user: Manager[];
  }>({ open: false, user: [] });
  const [loginAsModalData, setLoginAsModalData] = useState<{
    open: boolean;
    user: Manager[];
  }>({ open: false, user: [] });
  const [paginationManagers, setPaginationManagers] = useState<
    Pagination<Manager> | undefined
  >(undefined);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterManagersForm>(
    initialManagersColumnsData,
    initialFilterData,
    'firstName'
  );

  useEffect(() => {
    getManagers();
  }, [page, rowsPerPage, sortBy, sortType, filterData, searchTerm]);

  const getManagers = () => {
    managerService
      .getManagers(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationManagers(response.data);
        setLoading(false);
      });
  };

  const onDeactivateManager = (
    event: React.MouseEvent<unknown>,
    manager: Manager
  ) => {
    event.stopPropagation();
    setDeactivateModalData({ open: true, user: manager });
  };

  const onActivateManager = (
    event: React.MouseEvent<unknown>,
    manager: Manager
  ) => {
    event.stopPropagation();
    setActivateModalData({ open: true, user: manager });
  };

  const addManager = (event: React.MouseEvent<unknown>) => {
    if (isAdmin) {
      navigate(ADMINISTRATION_MANAGERS_CREATE);
    }
    event.stopPropagation();
    setAddUserModalData({ open: true });
  };

  const resendCredentials = (manager: Manager[]) => {
    setResendModalData({ open: true, user: manager });
  };

  const loginAs = (manager: Manager[]) => {
    setLoginAsModalData({ open: true, user: manager });
  };

  const onEditManager = (
    event: React.MouseEvent<unknown>,
    manager: Manager
  ) => {
    event.stopPropagation();
    if (isAdmin) {
      navigate(`${ADMINISTRATION_MANAGERS_EDIT}/${manager.id}`);
    } else {
      navigate(`${ADMINISTRATION_MANAGERS}/${manager.id}`);
    }
  };

  const onShowEdit = () => true;

  const onShowActivate = (manager: Manager) => {
    return !manager.active;
  };

  const onShowDeactivate = (manager: Manager) => {
    return !!manager.active;
  };

  const showCheckBox = (manager: Manager): boolean => !!manager.active;

  const onRowSelect = (manager: Manager[]) => {
    setSelectedManagers(manager);
  };

  const handleExport = () => {
    managerService.exportManagers(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'managers');
    });
  };

  const confirmUserActionStatus = (manager: Manager | undefined) => {
    if (manager?.id) {
      userService
        .changeUserStatus({
          id: manager.id,
          activate: !manager.active
        })
        .then(() => {
          getManagers();
          setDeactivateModalData({ open: false, user: undefined });
          setActivateModalData({ open: false, user: undefined });
        });
    }
  };

  const onConfirmResendCredentials = () => {
    const managerIds = selectedManagers.map((manager) => manager.id);
    userService.sendCredentials(managerIds as number[]).then(() => {
      setResendModalData({ open: false, user: selectedManagers });
      setSuccessModal(true);
    });
  };

  const onConfirmLogin = () => {
    if (selectedManagers[0].id && selectedManagers[0].keycloakId) {
      userService
        .loginAsUser(selectedManagers[0]?.keycloakId, 'allworknow')
        .then(() => {
          setResendModalData({ open: false, user: selectedManagers });

          location.reload();
        });
    }
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.managers) {
      body = {
        ...body,
        managers: filterData.managers
          ? filterData.managers.map((manager) => manager.id)
          : []
      };
    }

    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }

    return body;
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>View All Managers</PageTitle>
        <Chip size={paginationManagers?.totalElements} />
        <Box flexGrow={1} />
        <Button
          sx={{ height: '100%', mr: 3, mb: 3 }}
          variant="outlined"
          disabled={!selectedManagers.length}
          onClick={() => resendCredentials(selectedManagers)}
        >
          Send Credentials
        </Button>
        <Button
          sx={{ height: '100%', mr: 3, mb: 3 }}
          variant="outlined"
          disabled={!selectedManagers.length || !(selectedManagers.length < 2)}
          onClick={() => loginAs(selectedManagers)}
        >
          Login As User
        </Button>
        <Button sx={{ mb: 3 }} onClick={(e) => addManager(e)}>
          Add New User
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
        >
          {selectedManagers.length > 0 && (
            <Typography
              color="inherit"
              variant="subtitle1"
              component="div"
              mb={1}
            >
              {selectedManagers.length} selected
            </Typography>
          )}
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <FilterManagers
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationManagers?.content}
          rowClick={(manager: Manager) =>
            isAdmin
              ? navigate(`${ADMINISTRATION_MANAGERS_EDIT}/${manager.id}`)
              : navigate(`${ADMINISTRATION_MANAGERS}/${manager.id}`)
          }
          sortProps={{ sortBy, sortType, onRequestSort }}
          checkBoxes={{
            showCheckBox,
            selectRows: onRowSelect
          }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationManagers?.totalElements
          }}
          actionProps={{
            onEdit: onEditManager,
            onDeactivate: onDeactivateManager,
            onActivate: onActivateManager,
            showEditButton: onShowEdit,
            showDeactivateButton: onShowDeactivate,
            showActivateButton: onShowActivate,
            style: { padding: '11px' }
          }}
        />
        {!loading && !paginationManagers?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No users found!
          </Typography>
        )}
      </TableContainer>
      <DeactivateModal
        modalData={deactivateModalData}
        closeModal={() => setDeactivateModalData({ open: false })}
        confirmModal={() => confirmUserActionStatus(deactivateModalData.user)}
      />
      <ActivateModal
        modalData={activateModalData}
        closeModal={() => setActivateModalData({ open: false })}
        confirmModal={() => confirmUserActionStatus(activateModalData.user)}
      />
      <CreateManager
        modalData={addUserModalData}
        closeModal={() => setAddUserModalData({ open: false })}
        onSuccess={() => {
          getManagers();
        }}
      />
      <CredentialsModal
        modalData={resendModalData}
        closeModal={() => setResendModalData({ open: false, user: [] })}
        confirmModal={onConfirmResendCredentials}
      />
      <LoginAsModal
        modalData={loginAsModalData}
        closeModal={() => setLoginAsModalData({ open: false, user: [] })}
        confirmModal={onConfirmLogin}
      />
      <SuccessModal
        open={successModal}
        handleClose={() => setSuccessModal(false)}
      >
        <Typography textAlign="center">
          You have successfully re-sent login credentials.
        </Typography>
      </SuccessModal>
    </>
  );
};

export default ManagersList;
